import {
    FC,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { ArrowLeftIcon, ImageIcon, TradesIcon } from "../../assets/img/icons";
import ConnectedLayout from "../../components/ConnectedLayout";
import Header from "../../components/Header";
import Loader from "../../components/Loader";
import Text from "../../components/Text";
import { AuthContext } from "../../contexts/AuthContext";
import { getUserById } from "../../services/api/admin/user";
import { AdminUserByIdResponse } from "../../types/api/admin/user";
import {
    getSessionPlan,
    getTradeImage,
} from "../../services/api/tradingSessions";
import { Timeslots } from "../../types/enums/Timeslots";
import { getMoneyManagementLabel } from "../../types/enums/MoneyManagement";
import { useJournalSettings } from "../../services/journalSettings";
import { Session as SessionType } from "../../types/Session";
import {
    getEnNumericMonthYear,
    getFrenchDateFromString,
    isWithinSessionEditWindow,
    isWithinSessionDeleteWindow,
    parseInputDateToLocal,
} from "../../utils/date";
import TradesTable from "../Trades/TradesTable";

import { toast } from "react-toastify";
import Button from "../../components/Button";
import ConfirmModal from "../../components/ConfirmModal";
import TradingSessionFormModal from "../../components/ModalForm/TradingSessionFormModal";
import {
    deleteTradingSession,
    getTradingSessionById,
} from "../../services/api/tradingSessions";
import { isAdminRole } from "../../utils/graduation";
import { getUserName } from "../../utils/string";
import styles from "./styles.module.scss";

const Session: FC = () => {
    const [selectedTradeId, setSelectedTradeId] = useState<string | null>(null);

    const handleTradeClick = useCallback((tradeId: string) => {
        setSelectedTradeId(tradeId);
        // When a trade is selected, ensure session plan is hidden
        setIsSessionPlanOpen(false);
    }, []);
    const navigate = useNavigate();
    const { sessionId } = useParams<{ sessionId: string }>();
    const {
        getIsOwnDashboard,
        getUserIdFromQuery,
        isStreamOn,
        hasPermission,
        user,
    } = useContext(AuthContext)!;
    const { challengeMoneyManagement } = useJournalSettings();

    const [isLoading, setIsLoading] = useState(true);
    const [session, setSession] = useState<SessionType | undefined>();
    const [images, setImages] = useState<Record<string, string | undefined>>(
        {}
    );
    const [sessionPlanUrl, setSessionPlanUrl] = useState<string | undefined>();
    const [isSessionPlanOpen, setIsSessionPlanOpen] = useState<boolean>(false);
    const [userDashboard, setUserDashboard] = useState<
        AdminUserByIdResponse | undefined
    >();

    const [isTradingSessionModalOpen, setIsTradingSessionModalOpen] =
        useState<boolean>(false);

    const [confirmModal, setConfirmModal] = useState<{
        open: boolean;
        isLoading: boolean;
    }>({ open: false, isLoading: false });

    const userId = getUserIdFromQuery()!;
    const isOwnDashboard = getIsOwnDashboard();

    let title = "Chargement...";
    if (isOwnDashboard) {
        title = "Mes trades";
    } else if (userDashboard) {
        const { firstname, lastname, pseudo } = userDashboard;
        title = `Trades de ${getUserName(
            firstname ?? "",
            lastname ?? "",
            pseudo ?? "",
            isStreamOn
        )}`;
    }

    const dataUrlFromUrl = async (url: string): Promise<string> => {
        const res = await fetch(url);
        const blob = await res.blob();
        return await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
        });
    };

    const getImages = useCallback(async (userId: string, session: SessionType) => {
        if (!session) return;

        for (const trade of session.Trade) {
            try {
                const imageUrl = await getTradeImage(
                    userId,
                    session.id,
                    trade.id
                );
                const cacheKey = `tradeImage:${session.id}:${trade.id}`;
                let dataUrl: string | null = null;
                try {
                    dataUrl = sessionStorage.getItem(cacheKey);
                } catch {}

                if (!dataUrl && imageUrl?.url) {
                    try {
                        dataUrl = await dataUrlFromUrl(imageUrl.url);
                        try {
                            sessionStorage.setItem(cacheKey, dataUrl);
                        } catch {}
                    } catch {}
                }

                setImages((prev) => ({
                    ...prev,
                    [trade.id]: dataUrl || imageUrl?.url,
                }));
            } catch (error) {
                continue;
            }
        }
    }, []);

    const getSessionPlanUrl = async (userId: string, sessionId: string) => {
        try {
            const planResponse = await getSessionPlan(userId, sessionId);
            if (planResponse) {
                setSessionPlanUrl(planResponse.url);
            }
        } catch (error) {
            return undefined;
        }
    };

    const handleConfirmOpen = () => {
        setConfirmModal({ open: true, isLoading: false });
    };

    const handleConfirmClose = () => {
        setConfirmModal({
            open: false,
            isLoading: false,
        });
    };

    const handleConfirm = async () => {
        try {
            setConfirmModal({ ...confirmModal, isLoading: true });
            await deleteTradingSession(session!.id);
            toast.success(`Session supprimée avec succès`);
            if (isOwnDashboard) {
                navigate(`/trades?selectedMonth=${selectedMonth}`);
            } else {
                navigate(
                    `/trades?selectedMonth=${selectedMonth}&userId=${userId}`
                );
            }
        } catch (error) {
            toast.error(
                "Erreur lors de la suppression, veuillez réessayer plus tard"
            );
        } finally {
            setConfirmModal({ ...confirmModal, isLoading: false });
        }
    };

    const getSession = async (sessionId: string) => {
        getTradingSessionById({
            sessionId,
            userId,
        })
            .then((res) => {
                if (res) {
                    getImages(userId, res);
                    getSessionPlanUrl(userId, sessionId);
                    setSession(res);
                }
                setIsLoading(false);
            })
            .catch(() => {
                navigate("/trades");
            });
    };

    const handleCloseTradingSessionFormModal = () => {
        getSession(sessionId!);
        setIsTradingSessionModalOpen(false);
    };

    useEffect(() => {
        if (!userId || !sessionId) return;

        getTradingSessionById({
            sessionId,
            userId,
        })
            .then((res) => {
                if (res) {
                    getImages(userId, res);
                    if (res.sessionPlanKey) {
                        getSessionPlanUrl(userId, sessionId);
                    }
                    setSession(res);
                }
                setIsLoading(false);
            })
            .catch(() => {
                navigate("/trades");
            });
    }, [navigate, sessionId, userId, getImages]);

    useEffect(() => {
        if (!isOwnDashboard) {
            getUserById(userId!).then((user) => {
                setUserDashboard(user);
            });
        }
    }, [isOwnDashboard, userId]);

    const selectedMonth = useMemo(() => {
        if (session?.date) {
            const parsedDate = parseInputDateToLocal(session.date);
            if (parsedDate) {
                return getEnNumericMonthYear(parsedDate);
            }
        }

        return getEnNumericMonthYear(new Date());
    }, [session]);

    const canEdit = useMemo(() => {
        if (!session) return false;
        if (session.isWeekValidated) return false;

        const parsedDate = parseInputDateToLocal(session.date);
        const isWithinEditWindow = parsedDate
            ? isWithinSessionEditWindow(parsedDate)
            : false;
        return (
            isAdminRole(user?.role) ||
            (isOwnDashboard &&
                isWithinEditWindow &&
                hasPermission("trading-session", "update"))
        );
    }, [session, isOwnDashboard, user, hasPermission]);

    if (!sessionId) {
        return <Navigate to="/trades" replace />;
    }

    return (
        <ConnectedLayout>
            <TradingSessionFormModal
                mode="edit"
                open={isTradingSessionModalOpen}
                onClose={() => handleCloseTradingSessionFormModal()}
                session={session}
                setSession={setSession}
            />
            <ConfirmModal
                open={confirmModal.open}
                handleClose={handleConfirmClose}
                handleConfirm={handleConfirm}
                isLoading={confirmModal.isLoading}
            />
            <Header icon={TradesIcon} title={title} />
            <Button
                className={styles.backToCalendarButton}
                variant="transparent"
                size="large"
                Icon={ArrowLeftIcon}
                iconSize={16}
                onClick={() => {
                    if (isOwnDashboard) {
                        navigate(`/trades?selectedMonth=${selectedMonth}`);
                    } else {
                        navigate(
                            `/trades?selectedMonth=${selectedMonth}&userId=${userId}`
                        );
                    }
                }}
            >
                Revenir au calendrier
            </Button>
            {isLoading || !session ? (
                <Loader />
            ) : (
                <div className={styles.container}>
                    <div className={styles.tradesTableContainer}>
                        <div className={styles.sessionHeader}>
                            <div className={styles.sessionInfo}>
                                <Text variant="subtitle">
                                    {`Session du ${getFrenchDateFromString(
                                        session.date
                                    )} à ${
                                        Timeslots[
                                            session.timeslot as keyof typeof Timeslots
                                        ]
                                    }`}
                                </Text>
                                {session.moneyManagement != null && (
                                    <Text variant="body" className={styles.sessionMoneyManagement}>
                                        Money management :{" "}
                                        {getMoneyManagementLabel(
                                            session.moneyManagement,
                                            challengeMoneyManagement
                                        )}
                                    </Text>
                                )}
                                {sessionPlanUrl && (
                                    <div className={styles.sessionPlanToggle}>
                                        <Text variant="body-bold">
                                            Plan de session
                                        </Text>
                                        <ImageIcon
                                            onClick={() => {
                                                setIsSessionPlanOpen(
                                                    (prev) => !prev
                                                );
                                                setSelectedTradeId(null);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className={styles.sessionActions}>
                                {canEdit && (
                                    <Button
                                        className={styles.editSessionButton}
                                        variant="secondary"
                                        size="small"
                                        onClick={() =>
                                            setIsTradingSessionModalOpen(true)
                                        }
                                    >
                                        Éditer
                                    </Button>
                                )}
                                {!session.isWeekValidated &&
                                    (hasPermission(
                                        "admin-trading-sessions",
                                        "delete"
                                    ) ||
                                        (hasPermission(
                                            "trading-session",
                                            "delete"
                                        ) &&
                                            isWithinSessionDeleteWindow(
                                                session
                                            ))) && (
                                        <Button
                                            className={
                                                styles.deleteSessionButton
                                            }
                                            variant="tertiary"
                                            size="small"
                                            onClick={() => handleConfirmOpen()}
                                        >
                                            Supprimer
                                        </Button>
                                    )}
                            </div>
                        </div>
                        <TradesTable
                            className={styles.tradesTable}
                            trades={session.Trade || []}
                            isLoading={false}
                            onTradeClick={handleTradeClick}
                            images={images}
                        />
                    </div>
                    <div className={styles.tradeImageContainer}>
                        {sessionPlanUrl && isSessionPlanOpen ? (
                            <div
                                className={styles.tradeImage}
                                style={{ display: "flex", height: "100%" }}
                            >
                                <img
                                    src={sessionPlanUrl}
                                    alt={`Plan de session`}
                                    className={styles.tradeImage}
                                />
                            </div>
                        ) : (
                            Object.entries(images).map(
                                ([tradeId, imageUrl]) => (
                                    <div
                                        key={tradeId}
                                        className={styles.tradeImage}
                                        style={{
                                            display:
                                                selectedTradeId === tradeId
                                                    ? "flex"
                                                    : "none",
                                            height:
                                                selectedTradeId === tradeId
                                                    ? "100%"
                                                    : 0,
                                        }}
                                    >
                                        <img
                                            src={imageUrl}
                                            alt={`Trade ${tradeId}`}
                                            className={styles.tradeImage}
                                        />
                                    </div>
                                )
                            )
                        )}
                    </div>
                </div>
            )}
        </ConnectedLayout>
    );
};

export default Session;

